import React, { useEffect, useState } from "react";

import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import ShareIcon from "@mui/icons-material/Share";
import { Box, CircularProgress, IconButton, Tooltip } from "@mui/material";
import Button from "@mui/material/Button";
import CardContent from "@mui/material/CardContent";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";

import Card from "react-bootstrap/Card";
import { useNavigate } from "react-router-dom";

import EmptyState from "components/atoms/EmptyState";
import PageContainer from "components/atoms/PageContainer";
import { ConfirmationModal } from "components/organisms/ConfirmationModal";

import SwalAlertComp from "../../../components/SwalAlert";
import { deleteDestination } from "../../../helpers/leads/leadDestinations/deleteDestinations";
import { getDestinationIcon } from "./LeadDestinations.helpers";

const LeadDestinations = ({
    language,
    getLeadDestinationsStatus,
    destinations,
    dispatchGetAllDestinations,
}) => {
    const navigate = useNavigate();
    const [deletingId, setDeletingId] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingDeleteId, setPendingDeleteId] = useState(null);

    const handleConfirmDelete = async () => {
        if (!pendingDeleteId) return;
        setConfirmOpen(false);
        setDeletingId(pendingDeleteId);
        const response = await deleteDestination(pendingDeleteId);
        setDeletingId(null);
        setPendingDeleteId(null);
        if (response.status) {
            SwalAlertComp({
                title: language?.delete_swal_modal?.success ?? "Success",
                message: response.message,
                icon: "success",
                timer: 3000,
            });
            dispatchGetAllDestinations();
        } else {
            SwalAlertComp({
                title: language?.delete_swal_modal?.failed ?? "Failed",
                message: response.message,
                icon: "error",
            });
        }
    };

    useEffect(() => {
        dispatchGetAllDestinations();
    }, []);

    const handleCreateButton = () => {
        navigate("/leads-wizard");
    };

    const isLoading =
        getLeadDestinationsStatus === "idle" ||
        getLeadDestinationsStatus === "loading" ||
        getLeadDestinationsStatus === "failed";

    return (
        <PageContainer
            title={"Destinations"}
            subtitle={
                "On this page, you can view destinations, and create a new one by using the button."
            }
            additionalButtons={
                <Button
                    onClick={handleCreateButton}
                    color="primary"
                    variant="contained"
                >
                    {language?.destinations?.create_destination ??
                        "Create Destination"}
                </Button>
            }
            loading={isLoading}
            variant="list"
            containerClassName="tracker-detail-area"
        >
            <ConfirmationModal
                open={confirmOpen}
                onClose={() => {
                    setConfirmOpen(false);
                    setPendingDeleteId(null);
                }}
                onConfirm={handleConfirmDelete}
                title={
                    language?.delete_swal_modal?.are_you_sure ?? "Are you sure?"
                }
                message="This action cannot be undone."
                confirmColor="error"
            />

            {!destinations?.length ? (
                <EmptyState
                    icon={<ShareIcon />}
                    title={
                        language?.destinations?.no_destinations_yet ??
                        "No destinations yet"
                    }
                    description={
                        language?.destinations?.no_destinations_yet_desc ??
                        "Add your first destination to start sending your leads."
                    }
                />
            ) : (
            <Grid
                container
                rowSpacing={1}
                columnSpacing={{ xs: 1, sm: 2, md: 3 }}
            >
                {destinations &&
                    Array.isArray(destinations) &&
                    destinations.map((item, index) => {
                        const isDeleting = deletingId === item.id;
                        return (
                            <Grid
                                key={item?.id ?? index}
                                size={{
                                    xs: 12,
                                    md: 6,
                                    lg: 4,
                                }}
                            >
                                <Card
                                    className={`dashboard-tree-source-item lead-item service-item dashboard-tree-source-item-${index}`}
                                    style={{
                                        boxShadow: "none",
                                        border: "1px solid #E7E7E7",
                                        borderRadius: "10px",
                                        marginBottom: "20px",
                                        cursor: "default",
                                        position: "relative",
                                        height: "100%",
                                        display: "flex",
                                        flexDirection: "column",
                                    }}
                                >
                                    <div
                                        style={{
                                            position: "absolute",
                                            top: "10px",
                                            right: "10px",
                                            display: "flex",
                                            gap: "5px",
                                            zIndex: 10,
                                        }}
                                    >
                                        <Tooltip
                                            title={
                                                item?.has_connections
                                                    ? "The destination cannot be deleted due to an active connection."
                                                    : "Delete"
                                            }
                                        >
                                            <Box component="span">
                                                <IconButton
                                                    size="small"
                                                    disabled={
                                                        item?.has_connections ||
                                                        isDeleting
                                                    }
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        if (isDeleting) return;
                                                        setPendingDeleteId(
                                                            item.id,
                                                        );
                                                        setConfirmOpen(true);
                                                    }}
                                                    style={{
                                                        backgroundColor:
                                                            "#f5f5f5",
                                                        padding: "5px",
                                                    }}
                                                >
                                                    {isDeleting ? (
                                                        <CircularProgress
                                                            size={16}
                                                            color="error"
                                                        />
                                                    ) : (
                                                        <DeleteOutlineOutlinedIcon
                                                            fontSize="small"
                                                            color="error"
                                                        />
                                                    )}
                                                </IconButton>
                                            </Box>
                                        </Tooltip>
                                    </div>
                                    <CardContent
                                        style={{ padding: "20px" }}
                                        className="d-flex items-center justify-start"
                                    >
                                        <div
                                            style={{
                                                width: "2rem",
                                                height: "2rem",
                                            }}
                                            className="mr-3"
                                        >
                                            <img
                                                src={getDestinationIcon(
                                                    item?.type,
                                                )}
                                                alt={item?.type ?? ""}
                                            />
                                        </div>
                                        <Typography
                                            sx={{
                                                fontSize: 18,
                                                fontWeight: "700",
                                                fontFamily:
                                                    "Nunito, sans-serif",
                                                color: "black",
                                            }}
                                        >
                                            {item?.name}
                                        </Typography>
                                    </CardContent>

                                    <CardContent
                                        style={{
                                            padding: "20px",
                                            paddingTop: "0",
                                        }}
                                        className="d-flex items-center justify-start"
                                    >
                                        <span
                                            style={{
                                                display: "block",
                                                fontSize: "14px",
                                                color: "black",
                                                fontWeight: "500",
                                            }}
                                        >
                                            <span
                                                style={{
                                                    display: "block",
                                                    fontSize: "12px",
                                                    color: "#ABABAB",
                                                    fontWeight: "400",
                                                }}
                                            >
                                                {language?.destination_modal
                                                    ?.destination_type ??
                                                    "Destination Type"}
                                            </span>
                                            {item?.type}
                                        </span>
                                    </CardContent>

                                    {item?.created_at && (
                                        <CardContent
                                            style={{
                                                padding: "20px",
                                                paddingTop: "0",
                                            }}
                                            className="d-flex items-center justify-start"
                                        >
                                            <span
                                                style={{
                                                    display: "block",
                                                    fontSize: "14px",
                                                    color: "black",
                                                    fontWeight: "500",
                                                }}
                                            >
                                                <span
                                                    style={{
                                                        display: "block",
                                                        fontSize: "12px",
                                                        color: "#ABABAB",
                                                        fontWeight: "400",
                                                    }}
                                                >
                                                    {language?.lead
                                                        ?.create_on ??
                                                        "Create on"}
                                                </span>
                                                {item?.created_at}
                                            </span>
                                        </CardContent>
                                    )}

                                    <CardContent
                                        style={{
                                            padding: "20px",
                                            paddingTop: "0",
                                        }}
                                        className="d-flex items-center justify-start"
                                    >
                                        {item?.has_connections ? (
                                            <div className="in-use-area">
                                                {language?.destinations
                                                    ?.in_use ?? "In Use"}
                                            </div>
                                        ) : (
                                            <div className="not-in-use-area">
                                                {language?.destinations
                                                    ?.not_in_use ??
                                                    "Not In Use"}
                                            </div>
                                        )}
                                    </CardContent>
                                </Card>
                            </Grid>
                        );
                    })}
            </Grid>
            )}
        </PageContainer>
    );
};

export default LeadDestinations;
